import React from "react";
import styles from "./counter-number-gauge.module.scss";
import {
  GenericWidgetSemantics,
  OPCUAGaugeSemantics,
  WidgetPresentation,
  WidgetSemantics,
} from "@src/types/dashboards";
import { UnitDefinition } from "@src/types/units";
import { GaugeBoundValues } from "../use-last-widget-value";
import { buildGaugeZones, toDeg } from "./build-gauge-zones";
import { TimezoneDate } from "@src/components/atoms/timezone-date/timezone-date";

const TRACK_COLOR = "rgba(19, 27, 32, 0.6)";
const TARGET_COLOR = "#e6f1f2";

interface CounterNumberGaugeProps {
  title: string;
  semantics: WidgetSemantics;
  presentation: WidgetPresentation;
  unit?: UnitDefinition | null;
  value: number;
  /** Latest value of each node-driven bound (min / max / target), from
   *  `useLastWidgetValue`. All null when the widget drives its bounds with
   *  static numbers. */
  boundValues: GaugeBoundValues;
  /** ISO timestamp of the last fetched value (from the data's `time`
   *  field, surfaced by `useLastWidgetValue`). Rendered underneath the
   *  target row in inactiveGrey, and independently of it — the timestamp
   *  describes the data, not the target. Null when there's no real fetch
   *  yet (preview, placeholder, error) — we show no text in that case
   *  rather than a misleading timestamp, but the row itself always renders
   *  so the gauge keeps its height. */
  lastTimestamp?: string | null;
  showTitle?: boolean;
}

type GaugeSemantics = Partial<OPCUAGaugeSemantics & GenericWidgetSemantics>;

// Polar point on a 180° gauge: deg 0 = left (min), 180 = right (max).
const polar = (cx: number, cy: number, r: number, deg: number) => {
  const rad = ((deg - 180) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const arcPath = (
  cx: number,
  cy: number,
  r: number,
  startDeg: number,
  endDeg: number,
) => {
  const s = polar(cx, cy, r, startDeg);
  const e = polar(cx, cy, r, endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
};

const CX = 60;
const CY = 60;
const R = 50;

export const CounterNumberGauge = ({
  title,
  semantics,
  presentation,
  unit,
  value,
  boundValues,
  lastTimestamp,
  showTitle = true,
}: CounterNumberGaugeProps) => {
  const s = semantics as GaugeSemantics;
  // A bound a node drives wins over its static number. `boundValues` is null
  // for a bound that has no node binding, so the static value (or the dial's
  // own default) is used — and the widget only reaches the gauge once every
  // node-driven bound has a value, so a null here never means "still loading".
  const min = boundValues.min ?? s.min_value ?? 0;
  const max = boundValues.max ?? s.max_value ?? 100;
  const target = boundValues.target ?? s.target_manual ?? null;
  const threshold = s.thresholds ?? null;

  const unitText = unit?.unit_text?.eng;
  const unitLabel = unitText?.symbol || unitText?.name || "";
  // toFixed only accepts 0..100; template data is server-supplied.
  // Default to 2 decimals when the presentation doesn't override.
  const decimals = Math.min(
    100,
    Math.max(0, Math.trunc(presentation.decimal_places ?? 2)),
  );
  const safeValue = Number.isFinite(value) ? value : min;

  // Char-count-driven font sizing for the value text + unit suffix. Both
  // share the value's font (unit shrinks proportionally, current ratio
  // 7/15 ≈ 0.47) so the render block keeps a roughly constant horizontal
  // budget regardless of digit count: 1 digit grows to the size ceiling,
  // 4 digits renders at base 19, every char above the reference reduces
  // the font. Floor and ceiling are placeholders — bump them when you
  // have the visual feedback. ponytail: derived inline; lift to a
  // helper only if a second widget ever needs the same maths.
  const valueDisplayStr = safeValue.toFixed(decimals);
  const valueDisplayChars = valueDisplayStr.length;
  const unitDisplayChars = unitLabel?.length ?? 0;
  const totalDisplayChars = valueDisplayChars + unitDisplayChars;
  const displayBaseSize = 10;
  const displayRefChars = 4;
  const sizeFloor = 8;
  const sizeCeiling = 22;
  const valueFontSize = Math.max(
    sizeFloor,
    Math.min(
      sizeCeiling,
      Math.round(
        (displayBaseSize * displayRefChars) / Math.max(1, totalDisplayChars),
      ),
    ),
  );
  const unitFontSize = Math.max(
    6,
    Math.min(13, Math.round((valueFontSize * 7) / 15)),
  );

  const zones = buildGaugeZones(min, max, threshold);
  const valueDeg = toDeg(safeValue, min, max);
  const targetDeg = target != null ? toDeg(target, min, max) : null;

  // Needle mount + update animation: start the pointer at the min angle
  // (min maps to deg 0 → rotate -90) and sweep to the value. useEffect
  // runs after the first paint commits the -90 pose, so the CSS
  // transition has a previous angle to animate from; keyed on valueDeg so
  // every poll re-sweeps from the old angle to the new one.
  const [needleDeg, setNeedleDeg] = React.useState(-90);
  React.useEffect(() => {
    const id = requestAnimationFrame(() => setNeedleDeg(valueDeg - 90));
    return () => cancelAnimationFrame(id);
  }, [valueDeg]);

  return (
    <div className={styles["counter-number-gauge"]}>
      {showTitle && (
        <span className={styles["counter-number-gauge__title"]}>
          {title || "Gauge"}
        </span>
      )}

      <svg
        className={styles["counter-number-gauge__chart"]}
        viewBox="0 0 120 66"
        style={{ overflow: "visible" }}
      >
        <path
          d={arcPath(CX, CY, R, 0, 180)}
          stroke={TRACK_COLOR}
          strokeWidth="10"
          fill="none"
          strokeLinecap="round"
        />
        {zones.map((z, i) => (
          <path
            key={i}
            d={arcPath(CX, CY, R, z.from, z.to)}
            stroke={z.color}
            strokeWidth="10"
            fill="none"
            strokeLinecap="round"
          />
        ))}
        {targetDeg != null &&
          (() => {
            const a = polar(CX, CY, R - 8, targetDeg);
            const b = polar(CX, CY, R + 6, targetDeg);
            return (
              <line
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke={TARGET_COLOR}
                strokeWidth="2"
              />
            );
          })()}

        {/* value — drawn before the needle so the needle sits on top (z-order) */}
        <text
          x={CX}
          y={50}
          textAnchor="middle"
          className={styles["counter-number-gauge__svg-text"]}
          style={{ fontSize: valueFontSize, fontWeight: 700 }}
        >
          {valueDisplayStr}
          {unitLabel && (
            <tspan
              dx="2"
              className={styles["counter-number-gauge__svg-text"]}
              style={{ fontSize: unitFontSize, fontWeight: 600 }}
            >
              {unitLabel}
            </tspan>
          )}
        </text>

        <g
          style={{
            transform: `rotate(${needleDeg}deg)`,
            transformOrigin: `${CX}px ${CY}px`,
            transition: "transform 900ms ease-out",
          }}
        >
          {/* Tapered polygon: 6px-wide base near the hub, single-point tip
              at (60, 16) — sits on top of the hub circle so the visual is
              one cohesive needle shape. Default UP pose lines up with the
              existing rotation math (rotate(valueDeg-90) sweeps around
              (CX, CY)). */}
          <polygon
            points={`${CX},${CY - (R - 6)} ${CX - 3},${CY + 4} ${CX + 3},${
              CY + 4
            }`}
            className={styles["counter-number-gauge__svg-needle"]}
          />
          <circle
            cx={CX}
            cy={CY}
            r="4"
            className={styles["counter-number-gauge__svg-needle"]}
          />
        </g>

        {/* Min/max back INSIDE the SVG so their positions scale with
            the chart — proportional to viewBox. Anchored at (10, 66)
            and (110, 66): x is the same as the arc's path extremities
            (proportional), y sits at the viewBox bottom with
            dominant-baseline=hanging so the text hangs *below* the cap
            line. The arc cap ends at y=65 (60 mid + stroke half); text
            starts at y=66 → 1 px buffer between cap and label, no
            overlap. `overflow: visible` on the svg renders the text
            past the viewBox bottom. White, matching the rest of the
            gauge typography for weight continuity with the unit suffix
            under the value. */}
        <text
          x="13"
          y="68"
          textAnchor="end"
          dominantBaseline="hanging"
          className={styles["counter-number-gauge__svg-text"]}
          style={{ fontSize: "7px", fontWeight: 400 }}
        >
          {min}
        </text>
        <text
          x="107"
          y="68"
          textAnchor="start"
          dominantBaseline="hanging"
          className={styles["counter-number-gauge__svg-text"]}
          style={{ fontSize: "7px", fontWeight: 400 }}
        >
          {max}
        </text>
      </svg>

      <span className={styles["counter-number-gauge__target"]}>
        {target != null
          ? `Target: ${target}${unitLabel ? ` ${unitLabel}` : ""}`
          : ""}
      </span>
      <span className={styles["counter-number-gauge__timestamp"]}>
        {lastTimestamp ? (
          <TimezoneDate date={lastTimestamp} showRelative />
        ) : null}
      </span>
    </div>
  );
};
